import { Check } from 'lucide-react'

import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import {
  ENGAGEMENT_PHASES,
  engagementPhaseFor,
  gateIsClientFacingFor,
  gateStatusLabelFor,
} from '@/modules/architecture'
import { PHASES, phaseIndex, type PhaseKey } from '@/modules/phases'
import { cn } from '@/lib/utils'

/**
 * LOCKED — the 7-phase ribbon. Current phase tile is AUBERGINE (decided
 * furniture — mandatory fix #1; meridian was penalised for painting it mint).
 * Done tiles carry a check + name + sub-label at AA ink (the control's 3.43:1
 * sub-labels were penalised — hence text-muted-foreground, not faint).
 *
 * TASK-9F6C8EBE appended a GATE STRIP as a SIBLING beneath the `<ol>`. The
 * seven tiles keep their exact markup, tokens and geometry (AC-6.5) — the
 * strip is additive. It reconciles the two lifecycles in place: the gate's
 * NAME and exit criteria are Rev A's engagement phase, the KIND of sign-off is
 * this ribbon's own `PHASES[phase].gate`, and the position is computed from the
 * index rather than stored.
 */
export function PhaseRibbon({ current, className }: { current: PhaseKey; className?: string }) {
  const currentIdx = phaseIndex(current)
  return (
    <div className={className}>
      <ol className="flex gap-1.5" aria-label="Project phases">
      {PHASES.map((phase, i) => {
        const state = i < currentIdx ? 'done' : i === currentIdx ? 'current' : 'upcoming'
        return (
          <li
            key={phase.key}
            aria-current={state === 'current' ? 'step' : undefined}
            className={cn(
              'flex-1 rounded-md border px-2.5 py-2',
              state === 'current' && 'border-primary bg-primary text-primary-foreground',
              state === 'done' && 'border-border bg-card',
              state === 'upcoming' && 'border-border bg-transparent'
            )}
          >
            <div className="flex items-center gap-1 text-[0.8125rem] font-semibold">
              {state === 'done' && <Check className="h-3.5 w-3.5 text-done" aria-label="done" />}
              <span className={cn(state === 'upcoming' && 'text-muted-foreground')}>{phase.name}</span>
            </div>
            <div
              className={cn(
                'mt-0.5 truncate text-[0.6875rem]',
                state === 'current' ? 'text-white/75' : 'text-muted-foreground'
              )}
            >
              {phase.subLabel}
            </div>
          </li>
        )
      })}
      </ol>
      <GateStrip current={current} />
    </div>
  )
}

/** The gate the current phase is sitting on — name, status, criteria. */
function GateStrip({ current }: { current: PhaseKey }) {
  const engagement = engagementPhaseFor(current)

  return (
    <div
      data-testid="gate-strip"
      className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-card px-3 py-2"
    >
      <SectionLabel className="shrink-0">Gate</SectionLabel>
      <span className="font-mono text-[0.75rem] font-semibold tracking-[0.06em] text-foreground">
        {engagement.gateName}
      </span>
      <StatusPill tone={gateIsClientFacingFor(current) ? 'review' : 'neutral'}>
        {gateStatusLabelFor(current)}
      </StatusPill>
      <span className="text-xs text-muted-foreground">{engagement.gateCriteria}</span>
      <span className="ml-auto text-[0.6875rem] text-muted-foreground">
        Engagement phase {engagement.number} of {ENGAGEMENT_PHASES.length} · {engagement.name}
      </span>
    </div>
  )
}

